/**
 * BrandingPresetSelector Component
 *
 * Dropdown selector for built-in branding presets.
 * Allows quick application of predefined color/font themes.
 */

import type { ReactElement } from "react";
import { useState, useCallback } from "react";

import { BRANDING_PRESETS, PRESET_LABELS, PRESET_KEYS } from "./branding-presets";
import type { PresetKey, BrandingPreset } from "./branding-presets";

// ============================================================================
// Types
// ============================================================================

interface BrandingPresetSelectorProps {
  /** Callback when a preset is applied */
  readonly onApply: (preset: BrandingPreset) => void;
  /** Whether the control is disabled */
  readonly disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function BrandingPresetSelector({
  onApply,
  disabled = false,
}: BrandingPresetSelectorProps): ReactElement {
  const [selectedPreset, setSelectedPreset] = useState<PresetKey>("light");

  const handleApply = useCallback((): void => {
    const preset = BRANDING_PRESETS[selectedPreset];
    onApply(preset);
  }, [selectedPreset, onApply]);

  return (
    <div className="flex items-center gap-2" data-testid="preset-selector">
      <select
        value={selectedPreset}
        onChange={(e) => setSelectedPreset(e.target.value as PresetKey)}
        disabled={disabled}
        className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Select preset"
      >
        {PRESET_KEYS.map((key) => (
          <option key={key} value={key}>
            {PRESET_LABELS[key]}
          </option>
        ))}
      </select>
      <button
        onClick={handleApply}
        disabled={disabled}
        className="h-8 rounded-md bg-gray-100 px-3 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="apply-preset-button"
      >
        Apply Preset
      </button>
    </div>
  );
}

export type { BrandingPresetSelectorProps };
